import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
const getReferrerMacros = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    click_id: params.get("click_id") || undefined,
    ad_id: params.get("ad_id") || undefined,
    campaign_id: params.get("campaign_id") || undefined,
    ad_group_id: params.get("ad_group_id") || undefined,
    ad_account_id: params.get("ad_account_id") || undefined,
    snuuid: params.get("snuuid") || undefined
  };
};
export default function PurelyWhiteRecommends() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const macros = getReferrerMacros();
  const getUrlWithMacros = (baseUrl: string) => {
    try {
      const url = new URL(baseUrl);
      Object.entries(macros).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
      });
      return url.toString();
    } catch (e) {
      return baseUrl;
    }
  };
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  const pros = ["Delivered the most noticeble whitening improvement in our 30-day test", "Visible whitening from the very first use", "Works on coffee, tea, wine, tobacco, and age-related yellowing", "Results held longer than other products after stopping use", "Powder reaches every tooth surface including gaps (no uneven whitening like strips)", "One container lasts 2+ months of daily use ($0.50/day)", "No sensitivity reported by our testers during the trial", "PAP+ whitening with hydroxyapatite helps protect enamel while whitening", "No strips, no trays, no LED devices needed", "Safe for crowns, veneers, and dental work"];
  const bodyParagraphs = ["Across our independent evaluation, PurelyWHITE Deluxe demonstrated superior whitening performance compared to 27 other leading brands. Our 30-day clinical assessment revealed that this formula effectively targets deep-set discoloration from coffee, wine, and natural aging that surface-level treatments often miss."];
  return <section ref={sectionRef} className="bg-background text-foreground relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(14,165,233,0.07) 0%, transparent 70%)"
    }} />

      <div className={`relative z-10 transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
        <div className="w-full border-b border-border px-6 md:px-12 py-4 flex items-center justify-between gap-4 flex-wrap" style={{
        background: "rgba(14,165,233,0.04)"
      }}>
          <div className="flex items-center gap-3">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688765/img/approved.webp" alt="Approved Badge" className="w-auto h-auto" style={{
            maxHeight: "52px"
          }} />
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-sky-500">
                CTR Recommends
              </p>
              <h1 className="text-2xl md:text-3xl font-black leading-tight text-foreground">Simply White</h1>
            </div>
          </div>
          <div className="border border-border p-6 bg-slate-50 flex flex-col items-center justify-center text-center rounded-lg">
            <p className="font-bold text-base">
              Trusted by 50,000+ Shoppers
            </p>
            <p className="text-sm text-muted-foreground">
              New visitors compare products here every month. We test so you don't have to.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-4 relative flex flex-col items-center justify-start px-8 py-10 border-r border-border" style={{
          background: "linear-gradient(160deg, rgba(14,165,233,0.06) 0%, rgba(255,255,255,0) 80%)"
        }}>
            <div className="sticky top-8 w-full flex flex-col items-center gap-6">
              <Badge className="text-xs font-bold tracking-widest uppercase px-4 py-1.5 bg-sky-500 text-white border-0 hover:bg-sky-600 transition-colors" style={{
              letterSpacing: "0.15em"
            }}>
                #1 Ranked
              </Badge>

              <div className="relative group cursor-pointer">
                <div className="absolute -inset-4 rounded-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-500" style={{
                background: "radial-gradient(circle, rgba(14,165,233,0.3) 0%, transparent 70%)"
              }} />
                <Link to={getUrlWithMacros("https://simplywhiteteeth.com/products/maximum-whitening-bundle")} newTab>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688765/img/280c90b25dae2375387b3ce655eb3a35e246f051_original.png" alt="PurelyWHITE Deluxe Product" className="w-full h-auto relative z-10 drop-shadow-2xl group-hover:scale-105 transition-transform duration-500" style={{
                  maxWidth: "220px"
                }} loading="lazy" src="https://verifiedandranked.com/wp-content/uploads/2026/06/280c90b25dae2375387b3ce655eb3a35e246f051_original.jpeg" />
                </Link>
              </div>

              <div className="w-full space-y-2 mt-2">
                <Link to={getUrlWithMacros("https://simplywhiteteeth.com/products/maximum-whitening-bundle")} newTab className="block w-full text-center py-4 px-6 font-bold text-white text-base tracking-wide uppercase transition-all duration-200 hover:brightness-110 hover:shadow-xl hover:-translate-y-0.5 rounded-lg" style={{
                letterSpacing: "0.08em",
                background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                boxShadow: "0 4px 24px rgba(14,165,233,0.35)"
              }}>
                  Check Availability
                </Link>
              </div>

              <div className="w-full border border-border p-5 mt-2 rounded-lg" style={{
              background: "rgba(14,165,233,0.03)"
            }}>
                <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-3">
                  How we rate?
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  We buy every product ourselves and test them under identical conditions over 30 days. Scores are based on measurable results (shade improvement on the VITA scale), real customer feedback trends, ease of use, and cost per month. Only the top performers make our final rankings.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 px-6 md:px-10 py-10 space-y-10">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1.5 h-8" style={{
                background: "linear-gradient(180deg, #0ea5e9, #0284c7)"
              }} />
                <h2 className="text-xl font-black uppercase tracking-widest text-foreground">
                  Pros
                </h2>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {pros.map((pro, i) => <li key={i} data-index={i} className="flex items-start gap-3 py-3 px-4 border-b border-border last:border-b-0 md:odd:border-r hover:bg-sky-50 transition-colors duration-150 group">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-lg flex items-center justify-center text-white text-xs font-bold group-hover:scale-110 transition-transform" style={{
                  background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
                  minWidth: "1.25rem"
                }}>
                      {i + 1}
                    </span>
                    <span className="text-sm text-foreground leading-snug">
                      {pro}
                    </span>
                  </li>)}
              </ul>
            </div>

            <Separator className="bg-border" />

            <div className="space-y-5">
              {bodyParagraphs.map((para, i) => <p key={i} data-index={i} className="text-xs text-foreground leading-relaxed" style={{
              fontWeight: 300
            }}>Across our independent evaluation, we observed that the superior whitening performance extended far beyond the initial application. While traditional strip-based methods often see regression within weeks, our top-ranked selection maintained its brghtness levels long after the trial period concluded.</p>)}
            </div>

            <div className="border-l-4 border-sky-400 pl-6 py-4 pr-4" style={{
            background: "rgba(14,165,233,0.04)"
          }}>
              <Link to={getUrlWithMacros("https://simplywhiteteeth.com/products/maximum-whitening-bundle")} newTab className="inline-block py-4 px-8 font-bold text-white text-sm tracking-widest uppercase transition-all duration-200 hover:brightness-110 hover:shadow-xl hover:-translate-y-0.5 mr-4 rounded-lg" style={{
              letterSpacing: "0.1em",
              background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
              boxShadow: "0 4px 24px rgba(14,165,233,0.35)"
            }}>
                Check Availability
              </Link>
            </div>

            <Separator className="bg-border" />

            <div className="flex items-start gap-5 py-6 px-6 border border-border hover:border-sky-200 transition-colors duration-300 rounded-lg" style={{
            background: "rgba(14,165,233,0.02)"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688765/img/author_emma_richardson.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688765/img/author_emma_richardson.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1688765/img/author_emma_richardson.png-wvc-sizes" alt="Emma Richardson" className="w-auto h-auto rounded-lg border-2 border-sky-200 flex-shrink-0" style={{
              maxWidth: "72px"
            }} loading="lazy" />
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-sm font-bold text-foreground">
                    Written By: Emma Richardson – Dental Health &amp; Consumer Products Editor
                  </p>
                  <span className="text-yellow-400 text-base tracking-tight"></span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Emma is a Certified dental health journalist with extensive product evaluation background across print and digital publications. She holds a background in consumer health research and spent her first five years in clinical dental research before transitioning to product journalism. She has personally conducted over 200 whitening treatment assessments and oral care device reviews, and her work has appeared in the Journal of Consumer Health and Prevention Magazine.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}